import {
  ActivityId,
  type AnyUrn,
  DeliverableId,
  FileId,
  NotificationId,
  type NotificationUrlSlug,
  NotificationUrlSlugSchema,
  type NotificationUrn,
  ProjectId,
  ProjectServiceId,
  type ProjectSurveyType,
} from '@mntn-dev/domain-types'
import { isDefined } from '@mntn-dev/utilities'

import { route } from './route.ts'

export const notificationRoute = ({
  notificationId,
  slug,
}: {
  notificationId: NotificationId | NotificationUrn
  slug?: NotificationUrlSlug
}) => {
  return isDefined(slug)
    ? route('/notifications/:notificationId/:slug').params({
        notificationId: NotificationId.parse(notificationId),
        slug: NotificationUrlSlugSchema.parse(slug),
      })
    : route('/notifications/:notificationId').params({
        notificationId: NotificationId.parse(notificationId),
      })
}

export const projectRoute = ({
  projectId,
}: {
  projectId: ProjectId | AnyUrn
}) => {
  return route('/projects/:projectId').params({
    projectId: ProjectId.parse(projectId),
  })
}

export const projectActivityRoute = ({
  projectId,
  activityId,
}: {
  projectId: ProjectId | AnyUrn
  activityId: ActivityId | AnyUrn
}) => {
  return route('/projects/:projectId')
    .params({
      projectId: ProjectId.parse(projectId),
    })
    .query({ tab: 'activity', activityId: ActivityId.parse(activityId) })
}

export const projectFilesRoute = ({
  projectId,
}: {
  projectId: ProjectId | AnyUrn
}) => {
  return route('/projects/:projectId/files').params({
    projectId: ProjectId.parse(projectId),
  })
}

export const projectFileRoute = ({
  projectId,
  fileId,
}: {
  projectId: ProjectId | AnyUrn
  fileId: FileId | AnyUrn
}) => {
  return route('/projects/:projectId/files/:fileId').params({
    projectId: ProjectId.parse(projectId),
    fileId: FileId.parse(fileId),
  })
}

export const projectSurveyRoute = ({
  projectId,
  surveyType,
}: {
  projectId: ProjectId | AnyUrn
  surveyType: ProjectSurveyType
}) => {
  return route('/projects/:projectId/survey')
    .params({
      projectId: ProjectId.parse(projectId),
    })
    .query({ type: surveyType })
}

export const projectPostProductionReviewRoute = ({
  projectId,
  deliverableId,
}: {
  projectId: ProjectId | AnyUrn
  deliverableId?: DeliverableId | AnyUrn
}) => {
  return deliverableId
    ? route(
        '/projects/:projectId/post-production-review/:deliverableId'
      ).params({
        projectId: ProjectId.parse(projectId),
        deliverableId: DeliverableId.parse(deliverableId),
      })
    : route('/projects/:projectId/post-production-review').params({
        projectId: ProjectId.parse(projectId),
      })
}

export const projectServiceRoute = ({
  projectId,
  projectServiceId,
}: {
  projectId: ProjectId | AnyUrn
  projectServiceId: ProjectServiceId | AnyUrn
}) => {
  return route('/projects/:projectId/services/:projectServiceId').params({
    projectId: ProjectId.parse(projectId),
    projectServiceId: ProjectServiceId.parse(projectServiceId),
  })
}

export const projectPreProductionReviewRoute = ({
  projectId,
  projectServiceId,
}: {
  projectId: ProjectId | AnyUrn
  projectServiceId: ProjectServiceId | AnyUrn
}) => {
  return route('/projects/:projectId/services/:projectServiceId/review').params(
    {
      projectId: ProjectId.parse(projectId),
      projectServiceId: ProjectServiceId.parse(projectServiceId),
    }
  )
}

export const dashboardRoute = () => {
  return route('/dashboard')
}

export const authCallbackUrl = (redirect_url?: string) =>
  route('/auth/callback')
    .query({
      redirect_url:
        redirect_url ??
        dashboardRoute().toAbsoluteUrl(process.env.APP_BASE_URL),
    })
    .toAbsoluteUrl(process.env.APP_BASE_URL)

export const signOutRoute = (redirect_url?: string) =>
  route('/api/auth/sign-out').query({
    redirect_url: authCallbackUrl(redirect_url),
  })

export const signOutUrl = (redirect_url?: string) =>
  signOutRoute(redirect_url).toAbsoluteUrl(process.env.APP_BASE_URL)

export const signInRoute = (redirect_url?: string) =>
  route('/sign-in').query({
    redirect_url: authCallbackUrl(redirect_url),
  })

export const signInUrl = (redirect_url?: string) =>
  signInRoute(redirect_url).toAbsoluteUrl(process.env.APP_BASE_URL)
